import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Phone } from "@/components/PhoneFrame";

// A swipeable row of real screens, grouped by the app's four tabs. Native
// scroll with snap, so touch, trackpad and keyboard all work without a library.
// The tab chips jump to a group and follow the scroll; the arrows are for mouse
// users and only appear where there is room for them.
export default function ScreenCarousel({ screens, rooms }) {
  const ref = useRef(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const [room, setRoom] = useState(rooms?.[0]?.id);

  const padLeft = () => {
    const el = ref.current;
    return el ? parseFloat(getComputedStyle(el).paddingLeft) || 0 : 0;
  };

  const sync = () => {
    const el = ref.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 4);
    const end = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4;
    setAtEnd(end);
    const cards = [...el.querySelectorAll("[data-card]")];
    if (!cards.length) return;
    if (end) {
      setRoom(cards[cards.length - 1].dataset.room);
      return;
    }
    const edge = el.scrollLeft + padLeft() + 40;
    let current = cards[0];
    for (const c of cards) if (c.offsetLeft <= edge) current = c;
    setRoom(current.dataset.room);
  };

  useEffect(() => {
    sync();
    const el = ref.current;
    if (!el) return undefined;
    el.addEventListener("scroll", sync, { passive: true });
    window.addEventListener("resize", sync);
    return () => {
      el.removeEventListener("scroll", sync);
      window.removeEventListener("resize", sync);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const step = (dir) => {
    const el = ref.current;
    if (!el) return;
    const card = el.querySelector("[data-card]");
    const w = card ? card.getBoundingClientRect().width + 24 : el.clientWidth * 0.8;
    el.scrollBy({ left: dir * w * 2, behavior: "smooth" });
  };

  const jump = (id) => {
    const el = ref.current;
    const card = el?.querySelector(`[data-card][data-room="${id}"]`);
    if (!el || !card) return;
    el.scrollTo({ left: card.offsetLeft - padLeft(), behavior: "smooth" });
  };

  const label = (id) => rooms?.find((r) => r.id === id)?.label || "";
  const arrow =
    "hidden md:flex items-center justify-center w-11 h-11 rounded-full border border-dharma-gold/50 text-dharma-gold-deep hover:bg-dharma-gold hover:text-cream transition-colors disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-dharma-gold-deep";

  return (
    <div>
      <div className="flex items-center justify-between gap-4 px-5 md:px-10 lg:px-14 mb-8 md:mb-10">
        <div className="flex gap-1.5 sm:gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden -mx-1 px-1 py-1">
          {(rooms || []).map((r) => {
            const on = r.id === room;
            return (
              <button
                key={r.id}
                type="button"
                onClick={() => jump(r.id)}
                aria-pressed={on}
                className={`shrink-0 min-h-[44px] px-3.5 sm:px-5 rounded-full border font-body text-[0.68rem] sm:text-[0.78rem] tracking-[0.14em] sm:tracking-widest2 uppercase transition-colors ${
                  on
                    ? "bg-ink text-cream border-ink"
                    : "border-dharma-gold/70 text-dharma-gold-deep bg-white/40 hover:bg-dharma-gold/15"
                }`}
              >
                {r.label}
              </button>
            );
          })}
        </div>
        <div className="hidden md:flex gap-3 shrink-0">
          <button type="button" className={arrow} onClick={() => step(-1)} disabled={atStart} aria-label="Earlier screens">
            <ChevronLeft size={18} />
          </button>
          <button type="button" className={arrow} onClick={() => step(1)} disabled={atEnd} aria-label="More screens">
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
      <div
        ref={ref}
        tabIndex={0}
        role="region"
        aria-label="Screens from the Dharma app"
        className="flex gap-6 overflow-x-auto snap-x snap-mandatory scroll-px-5 md:scroll-px-10 lg:scroll-px-14 px-5 md:px-10 lg:px-14 pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-dharma-gold/60 rounded-sm"
      >
        {screens.map((s) => (
          <figure
            key={s.src}
            data-card
            data-room={s.room}
            className="snap-start shrink-0 w-[62vw] max-w-[250px] md:max-w-none md:w-[calc((100vw-5.5rem)/2.5)] lg:w-[calc((100vw-9.5rem)/4.4)] 2xl:w-[calc((100vw-9.5rem)/5.4)] 2xl:max-w-[300px]"
          >
            <Phone src={s.src} alt={s.alt} />
            <figcaption className="mt-5 px-1">
              <span className="eyebrow block mb-1.5">{label(s.room)}</span>
              <span className="font-display text-lg md:text-xl text-ink leading-snug tracking-tightish block">
                {s.caption}
              </span>
            </figcaption>
          </figure>
        ))}
        <div className="shrink-0 w-1" aria-hidden="true" />
      </div>
    </div>
  );
}
